'use client';

import React, { AnchorHTMLAttributes, ElementType } from 'react';

import classNames from 'classnames';

import { CONTENT_TYPE_LINK } from '@/core/constants';
import { TypeNavItemFields } from '@/core/generated-types';

import useLocale from '@/hooks/useLocale';

import { Link } from '@/navigation';

import { buildLinkUrl } from '@/utils/linkBuilder';

import {
  CLICKED_ITEM_TYPES,
  PAGE_LOCATIONS,
  trackItemClickedEvent,
} from '@/utils/rudderstack';

interface NavItemProps extends TypeNavItemFields {
  onClose?: () => void;
}

export const NavItem = ({
  badgeText,
  badgeColor,
  link,
  title,
  onClose,
}: NavItemProps) => {
  const locale = useLocale();

  const href = buildLinkUrl(link, locale);

  const badgeColorClass =
    badgeColor?.fields?.color && `text-${badgeColor.fields.color}`;

  const LinkComponent =
    link?.sys.contentType.sys.id === CONTENT_TYPE_LINK.EXTERNAL
      ? ('a' as ElementType<AnchorHTMLAttributes<HTMLAnchorElement>>)
      : Link;

  return (
    <LinkComponent
      href={href}
      data-testid="nav-item"
      className="flex items-center text-base md:text-sm md:py-1 py-2.5"
      onClick={() => {
        trackItemClickedEvent({
          type: CLICKED_ITEM_TYPES.LINK,
          cta: title ?? '',
          pageLocation: PAGE_LOCATIONS.NAVIGATION,
          url: window.location.href,
        });

        onClose?.();
      }}
    >
      <div className="text-charcoal-v2 font-normal flex-none lg:hover:text-blue-deep lg:hover:underline w-fit">
        {title}
      </div>

      {badgeText && (
        <div
          className={classNames('flex-none ml-2 font-bold', badgeColorClass)}
        >
          {badgeText}
        </div>
      )}
    </LinkComponent>
  );
};

export default React.memo(NavItem);
